import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import {
  CHAT_TOOL_UI_NAMES,
  renderToolPart,
} from '@/components/chat/messages'
import { ChatStoppedContext } from '@/components/chat/chat-stopped-context'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/dev/tool-ui'
)({
  component: ToolUiHarnessRoute,
})

const WATCHLIST_TOOLS = [
  'checksScan',
  'testsListChildren',
  'testsAssignChild',
  'testsCreate',
  'evidenceSubmitForReview',
  'evidenceWithdrawSubmission',
  'risksLinkControl',
  'risksUnlinkControl',
  'risksLinkAsset',
  'risksUnlinkAsset',
  'risksLinkVendor',
  'risksUnlinkVendor',
  'risksUpdate',
  'driftPropose',
  'driftAccept',
  'driftDismiss',
  'audit',
  'auditGetActivityFeed',
  'auditGetLogs',
]

const BACKEND_CHAT_TOOL_NAMES = [
  'getPageContent',
  'presentOptions',
  'program',
  'programGetState',
  'programGetReadiness',
  'programGetTimeline',
  'auditPackageReadiness',
  'evidence',
  'evidenceListEvidence',
  'evidenceGetEvidence',
  'evidenceFulfillWithPolicy',
  'evidenceFulfillWithDocument',
  'evidenceFulfillTest',
  'evidenceSubmitForReview',
  'evidenceWithdrawSubmission',
  'evidenceReviewSubmission',
  'evidenceApproveWithStatusSync',
  'policies',
  'policiesList',
  'policiesGetContent',
  'policiesGround',
  'policiesGenerate',
  'policiesProposeApplyBlocks',
  'claims',
  'claimsList',
  'claimsFind',
  'claimsRecord',
  'claimsEdit',
  'claimsDelete',
  'cis',
  'cisLookup',
  'checksGetCheck',
  'checksListChecks',
  'checksLinkedTests',
  'checksHistory',
  'checksScan',
  'checksCode',
  'checksSaveFinding',
  'checksDetectDrift',
  'checksRemedyPrepare',
  'checksRemedyValidate',
  'checksRemedyExecute',
  'checksRemedyUndo',
  'scheduling',
  'schedulingList',
  'schedulingGetDeadlines',
  'findings',
  'findingsTriage',
  'findingsSearch',
  'findingsGet',
  'findingsVerify',
  'findingsClose',
  'findingsUpdate',
  'findingsAcceptRisk',
  'findingsFalsePositive',
  'driftPropose',
  'driftAccept',
  'driftDismiss',
  'tickets',
  'ticketsList',
  'ticketsGet',
  'ticketsCreate',
  'ticketsUpdate',
  'ticketsClose',
  'ticketsDelete',
  'proposeRemediation',
  'executeRemediationPlan',
  'undoRemediation',
  'vendors',
  'vendorsList',
  'vendorsGet',
  'vendorsSearch',
  'vendorsCreate',
  'vendorsAssess',
  'vendorsRequestEvidence',
  'risks',
  'risksList',
  'risksGet',
  'risksSearch',
  'risksStats',
  'risksLinkControl',
  'risksUnlinkControl',
  'risksLinkAsset',
  'risksUnlinkAsset',
  'risksLinkVendor',
  'risksUnlinkVendor',
  'risksUpdate',
  'assets',
  'assetsList',
  'assetsGet',
  'assetsSearch',
  'assetsStats',
  'assetsAnalyzeGap',
  'assetsCreate',
  'assetsUpdate',
  'assetsClassify',
  'assetsMarkReviewed',
  'assetsListProviders',
  'assetsSyncNow',
  'documents',
  'documentsSaveDraft',
  'documentsList',
  'documentsSearch',
  'documentsGetById',
  'tests',
  'testsList',
  'testsGet',
  'testsAssign',
  'testsUpdateStatus',
  'testsComplete',
  'testsSetDueDate',
  'testsListChildren',
  'testsAssignChild',
  'testsBulkAssign',
  'testsCreate',
  'testsConvert',
  'apiTestRunStart',
  'runbook',
  'runbookSearch',
  'runbookGet',
  'runbookLink',
  'runbookUpdate',
  'runbookExecution',
  'runbookExecutionExecute',
  'runbookExecutionStatus',
  'runbookExecutionResume',
  'runbookExecutionApprove',
  'runbookExecutionReject',
  'population',
  'populationListUsers',
  'populationListPrivilegedUsers',
  'populationListCodeAccessUsers',
  'populationListIdentityCenterUsers',
  'populationListOrgMembers',
  'populationListEmployees',
  'populationListAccessRoleHolders',
  'populationListComplianceRoleHolders',
  'populationFindEmployee',
  'populationListDevices',
  'populationList',
  'populationDiff',
  'integrations',
  'integrationsList',
  'integrationsGet',
  'integrationsGetRepoSettings',
  'integrationsRequestConnect',
  'artifacts',
  'artifactsSave',
  'audit',
  'auditGetActivityFeed',
  'auditGetLogs',
  'sendEmail',
]

const LEGACY_TOOL_INVOCATION_TOOLS = new Set([
  'addSource',
  'article_generation',
  'citationSearch',
  'findInternalDoc',
  'glossarySearch',
  'searchKnowledge',
  'topic_suggestion',
  'suggest_topic_with_content',
  'webSearch',
])

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
}

function makeToolPart(
  toolName: string,
  state: 'input-available' | 'output-available' | 'error'
) {
  const input = sampleInput(toolName)
  if (LEGACY_TOOL_INVOCATION_TOOLS.has(toolName)) {
    return {
      type: 'tool-invocation',
      toolInvocation: {
        toolCallId: `dev-${toolName}-${state}`,
        toolName,
        args: input,
        state: state === 'output-available' ? 'result' : 'call',
        result: state === 'output-available' ? sampleOutput(toolName) : null,
        error: state === 'error' ? `Mock failure for ${toolName}` : null,
      },
    }
  }
  const base = {
    type: `tool-${toolName}`,
    toolCallId: `dev-${toolName}-${state}`,
    input,
    state,
  }
  if (state === 'output-available') {
    return {
      ...base,
      output: sampleOutput(toolName),
    }
  }
  if (state === 'error') {
    return {
      ...base,
      errorText: `Mock failure for ${toolName}`,
    }
  }
  return base
}

function sampleInput(toolName: string): Record<string, unknown> {
  if (toolName.includes('Search')) return { query: 'mfa', limit: 5 }
  if (toolName.includes('Get')) return { id: 'demo-001' }
  if (toolName.includes('Assign')) return { testId: 'test-001', assignee: 'Alex' }
  if (toolName.includes('Create')) return { title: 'Review MFA evidence' }
  if (toolName.includes('Update')) return { id: 'demo-001', status: 'open' }
  if (toolName.includes('Delete')) return { id: 'demo-001' }
  if (toolName.includes('Drift') || toolName.startsWith('drift')) {
    return { driftId: 'drift-001', target: 'Access Control Policy' }
  }
  if (toolName.startsWith('audit')) return { limit: 5 }
  if (toolName.startsWith('risks')) return { riskId: 'RISK-001' }
  if (toolName.startsWith('tests')) return { testId: 'TEST-001' }
  if (toolName.startsWith('checks')) return { checkId: 'CHK-001' }
  return { id: 'demo-001' }
}

function sampleOutput(toolName: string): Record<string, unknown> {
  if (toolName.startsWith('risks')) {
    return {
      success: true,
      count: 2,
      total: 2,
      risks: [
        {
          id: 'risk-1',
          displayId: 'RISK-001',
          title: 'Privileged access is not reviewed',
          riskRating: 'high',
          status: 'open',
          category: 'Access',
          ownerLabel: 'CTO',
        },
        {
          id: 'risk-2',
          displayId: 'RISK-002',
          title: 'Vendor review is stale',
          riskRating: 'medium',
          status: 'treatment planned',
          category: 'Vendor',
          ownerLabel: 'Ops',
        },
      ],
    }
  }
  if (toolName.startsWith('vendors')) {
    return {
      success: true,
      count: 2,
      vendors: [
        {
          displayId: 'VEN-001',
          name: 'GitHub',
          service: 'Source control',
          riskTier: 'high',
          status: 'approved',
          category: 'Engineering',
        },
        {
          displayId: 'VEN-002',
          name: 'AWS',
          service: 'Cloud hosting',
          riskTier: 'high',
          status: 'approved',
          category: 'Infrastructure',
        },
      ],
    }
  }
  if (toolName.startsWith('assets')) {
    return {
      success: true,
      count: 2,
      total: 2,
      assets: [
        {
          displayId: 'AST-001',
          name: 'Production API',
          assetType: 'application',
          criticality: 'critical',
          environment: 'production',
          ownerLabel: 'Engineering',
        },
        {
          displayId: 'AST-002',
          name: 'Customer database',
          assetType: 'database',
          criticality: 'high',
          environment: 'production',
          ownerLabel: 'Platform',
        },
      ],
    }
  }
  if (toolName.startsWith('policies')) {
    return {
      success: true,
      policies: [
        {
          slug: 'access-control-policy',
          title: 'Access Control Policy',
          status: 'draft',
          necessity: 'required',
          controlRefs: ['CC6.1', 'CC6.2'],
        },
        {
          slug: 'vendor-management-policy',
          title: 'Vendor Management Policy',
          status: 'published',
          necessity: 'required',
          controlRefs: ['CC9.2'],
        },
      ],
    }
  }
  if (toolName.startsWith('findings')) {
    return {
      success: true,
      count: 2,
      findings: [
        {
          id: 'finding-1',
          findingKey: 'FND-001',
          title: 'MFA evidence missing',
          severity: 'high',
          status: 'open',
          controlRef: 'CC6.1',
        },
        {
          id: 'finding-2',
          findingKey: 'FND-002',
          title: 'Access review overdue',
          severity: 'medium',
          status: 'triaged',
          controlRef: 'CC6.2',
        },
      ],
    }
  }
  if (toolName.startsWith('tickets')) {
    return {
      success: true,
      count: 2,
      tickets: [
        {
          id: 'ticket-1',
          key: 'SEC-101',
          title: 'Upload MFA screenshots',
          status: 'open',
          provider: 'Linear',
          priority: 'high',
          assigneeName: 'Alex',
          url: 'https://linear.app/example/issue/SEC-101',
        },
        {
          id: 'ticket-2',
          key: 'SEC-102',
          title: 'Review admin users',
          status: 'in progress',
          provider: 'Linear',
          priority: 'medium',
          assigneeName: 'Sam',
          url: 'https://linear.app/example/issue/SEC-102',
        },
      ],
    }
  }
  if (toolName.startsWith('tests')) {
    return {
      success: true,
      count: 2,
      tests: [
        { id: 'TEST-001', title: 'Verify MFA enforcement', status: 'open' },
        { id: 'TEST-002', title: 'Review admin access', status: 'complete' },
      ],
      children: [
        { id: 'SUB-001', title: 'Collect screenshot', status: 'open' },
      ],
      message: 'Test operation completed',
    }
  }
  if (toolName.startsWith('evidence')) {
    return {
      success: true,
      count: 1,
      evidence: [
        {
          id: 'EV-001',
          title: 'MFA settings screenshot',
          status: 'submitted',
        },
      ],
      message: 'Evidence operation completed',
    }
  }
  if (toolName.startsWith('audit')) {
    return {
      success: true,
      count: 2,
      logs: [
        {
          id: 'AUD-001',
          action: 'policy.generated',
          actor: 'Vera',
          timestamp: '2026-05-20T10:00:00Z',
        },
        {
          id: 'AUD-002',
          action: 'evidence.submitted',
          actor: 'Alex',
          timestamp: '2026-05-20T10:05:00Z',
        },
      ],
    }
  }
  if (toolName.startsWith('drift')) {
    return {
      success: true,
      driftId: 'DRIFT-001',
      status: toolName.endsWith('Accept')
        ? 'accepted'
        : toolName.endsWith('Dismiss')
          ? 'dismissed'
          : 'proposed',
      message: 'Drift operation completed',
    }
  }
  return {
    success: true,
    ok: true,
    count: 1,
    message: `${toolName} completed`,
  }
}

type ToolUxSpec = {
  intent: string
  chatUx: string
  productSurface: string
  manualTest: string
  href?: string
}

function getToolUxSpec(toolName: string, workspaceId: string): ToolUxSpec {
  const workspacePath = `/workspaces/${workspaceId}`
  if (toolName === 'documentsSaveDraft') {
    return {
      intent: 'Let Vera draft a vault document and ask the user to save, discard, or reopen it.',
      chatUx: 'Rich draft card with pending/saved/discarded states and editor actions.',
      productSurface: 'Chat bubble opens the document draft workflow; saved documents live in Vault > Documents.',
      manualTest: 'Search documentsSaveDraft, confirm Completed is a draft action card rather than a plain status line.',
      href: `${workspacePath}/documents`,
    }
  }
  if (toolName.startsWith('risks')) {
    return {
      intent: 'Read or update the risk register from chat.',
      chatUx: toolName.includes('List') || toolName.includes('Search')
        ? 'Status line plus list preview; Get opens a detail overlay; mutations should show an action status row.'
        : 'Status line; detail tools may open the risk detail overlay.',
      productSurface: 'Risk register and risk detail pages.',
      manualTest: 'Click the tool and make sure it does not fall back to “Tool completed”. For list/search, confirm rows appear.',
      href: `${workspacePath}/risks`,
    }
  }
  if (toolName.startsWith('vendors')) {
    return {
      intent: 'Search, list, or inspect vendors from chat.',
      chatUx: 'List/search show a compact preview; Get opens vendor detail; writes show status.',
      productSurface: 'Vendor management page and vendor detail panel.',
      manualTest: 'Use vendorsList/vendorsSearch and confirm the preview names vendors directly in the tool bubble.',
      href: `${workspacePath}/vendors`,
    }
  }
  if (toolName.startsWith('assets')) {
    return {
      intent: 'Search, list, classify, sync, or inspect assets.',
      chatUx: 'List/search show a compact preview; Get opens asset detail; sync/classify/write tools show status.',
      productSurface: 'Asset inventory page and asset detail panel.',
      manualTest: 'Use assetsList/assetsSearch and confirm the preview appears; use assetsGet to confirm an Open details affordance.',
      href: `${workspacePath}/assets`,
    }
  }
  if (toolName.startsWith('policies')) {
    return {
      intent: 'Find, read, generate, or apply policy content.',
      chatUx: toolName === 'policiesProposeApplyBlocks'
        ? 'Rich apply-blocks diff card with Apply controls.'
        : 'List preview or policy status line; content reads should expose policy details.',
      productSurface: 'Policies page and policy editor/detail view.',
      manualTest: 'For policiesList, confirm policy names are visible. For policiesProposeApplyBlocks, confirm a rich apply UI appears.',
      href: `${workspacePath}/policies`,
    }
  }
  if (toolName.startsWith('evidence')) {
    return {
      intent: 'Find evidence, fulfill tests, or move evidence through review.',
      chatUx: 'Evidence status line or review action row; detail tools should expose evidence context.',
      productSurface: 'Evidence and test detail submission surfaces.',
      manualTest: 'Check submit/withdraw/review tools for explicit review wording, not generic fallback.',
      href: `${workspacePath}/evidence`,
    }
  }
  if (toolName.startsWith('tests') || toolName === 'apiTestRunStart') {
    return {
      intent: 'List, inspect, assign, create, convert, or complete compliance tests and subtasks.',
      chatUx: 'Status line, list/subtask preview, or action row depending on the operation.',
      productSurface: 'Tests page and test detail pages.',
      manualTest: 'Search testsListChildren/testsAssignChild/testsCreate and verify they render as test-specific UI.',
      href: `${workspacePath}/tests`,
    }
  }
  if (toolName.startsWith('checks')) {
    return {
      intent: 'Run scanner/check actions and prepare or execute remediation.',
      chatUx: 'Status line today; scan/execute tools are candidates for progress UI.',
      productSurface: 'Checks/settings and findings/remediation surfaces.',
      manualTest: 'Search checksScan and confirm it has a scan-specific status, not fallback.',
      href: `${workspacePath}/settings/checks`,
    }
  }
  if (toolName.startsWith('findings')) {
    return {
      intent: 'Search, triage, verify, close, or classify compliance findings.',
      chatUx: 'Search should show finding previews; writes should show explicit action rows and approval when required.',
      productSurface: 'Findings are surfaced through risks, checks, and tickets workflows.',
      manualTest: 'Use findingsSearch and confirm severity/status/control are visible in the preview.',
      href: `${workspacePath}/risks`,
    }
  }
  if (toolName.startsWith('tickets')) {
    return {
      intent: 'Read or manage external work tickets from chat.',
      chatUx: 'List preview with external links; create/update/close/delete show action status.',
      productSurface: 'Task/ticket surfaces and connected Linear/Jira/GitHub issue links.',
      manualTest: 'Use ticketsList and confirm titles plus provider/status show in the preview.',
      href: `${workspacePath}/tasks`,
    }
  }
  if (toolName.startsWith('runbook')) {
    return {
      intent: 'Search, link, execute, or approve runbooks.',
      chatUx: 'Runbook proposals render a draft card; execution can show input-needed or run content states.',
      productSurface: 'Runbooks page and run execution pages.',
      manualTest: 'Use proposeRunbook/runbookExecutionExecute and confirm specialized runbook UI appears.',
      href: `${workspacePath}/runbooks`,
    }
  }
  if (toolName.startsWith('population')) {
    return {
      intent: 'Inspect scoped user populations across providers.',
      chatUx: 'Population list/status line; future enhancement can preview users and provider context.',
      productSurface: 'Populations page.',
      manualTest: 'Use population list tools and confirm provider/count details are visible.',
      href: `${workspacePath}/populations`,
    }
  }
  if (toolName.startsWith('integrations') || toolName.includes('Connect')) {
    return {
      intent: 'Inspect integrations or guide the user to connect a provider.',
      chatUx: 'Connect request should show connect actions or open the appropriate side panel.',
      productSurface: 'Connect Tools / Integrations pages.',
      manualTest: 'Use integrationsRequestConnect and confirm there is an actionable connect UI.',
      href: `${workspacePath}/connect-tools`,
    }
  }
  if (toolName.startsWith('drift')) {
    return {
      intent: 'Propose, accept, or dismiss detected compliance drift.',
      chatUx: 'Should be at least a drift-specific status/action row; fallback means missing UI.',
      productSurface: 'Policy/check drift remediation flow.',
      manualTest: 'Search driftPropose/driftAccept/driftDismiss; any generic “Tool completed” is a bug to fix.',
      href: `${workspacePath}/policies`,
    }
  }
  if (toolName.startsWith('audit')) {
    return {
      intent: 'Read audit activity and logs.',
      chatUx: 'Should show an audit-specific status row, and ideally a short activity preview.',
      productSurface: 'Audits / auditor center surfaces.',
      manualTest: 'Search auditGetActivityFeed/auditGetLogs; generic fallback means the audit UI is missing.',
      href: `${workspacePath}/audits`,
    }
  }
  if (toolName.startsWith('scheduling')) {
    return {
      intent: 'Create, list, delete, or inspect compliance deadlines.',
      chatUx: 'Status line; deadline checks can expand overdue/stale items.',
      productSurface: 'Program timeline, tasks, and readiness workflows.',
      manualTest: 'Use schedulingGetDeadlines and confirm overdue/stale details can be shown.',
      href: `${workspacePath}/readiness`,
    }
  }
  if (toolName.startsWith('program')) {
    return {
      intent: 'Summarize program state, readiness, roles, timeline, and scoped populations.',
      chatUx: 'Compact status line with key detail; LLM may synthesize a broader markdown summary.',
      productSurface: 'Program/readiness dashboard.',
      manualTest: 'Use programGetReadiness/programGetState and confirm the key metric appears in the row.',
      href: `${workspacePath}/readiness`,
    }
  }
  if (toolName.startsWith('claims')) {
    return {
      intent: 'Find, record, edit, or delete policy claims.',
      chatUx: 'Status line or claim list/detail preview.',
      productSurface: 'Policy detail and control evidence linkage surfaces.',
      manualTest: 'Use claimsList/claimsFind and confirm claim-specific wording.',
      href: `${workspacePath}/policies`,
    }
  }
  if (toolName === 'sendEmail') {
    return {
      intent: 'Draft or send an email with human confirmation.',
      chatUx: 'Rich email draft/sent card.',
      productSurface: 'Chat confirmation flow, not a standalone page.',
      manualTest: 'Confirm the completed state looks like an email draft card.',
    }
  }
  if (toolName === 'presentOptions' || toolName === 'presentQuestion') {
    return {
      intent: 'Ask the user to choose from structured options.',
      chatUx: 'Question text in chat; choices may render in the sidecar/pending action area.',
      productSurface: 'Chat decision flow.',
      manualTest: 'Confirm the UI clearly asks the question and does not look like a raw tool result.',
    }
  }
  return {
    intent: 'General chat tool interaction.',
    chatUx: 'Should render a tool-specific row/card. Generic fallback means the tool is not mapped.',
    productSurface: 'Depends on the selected tool namespace.',
    manualTest: 'Select the tool and inspect running/completed/error. If it says “Tool completed: name”, add a renderer.',
  }
}

function ToolUiHarnessRoute() {
  const { workspaceId } = Route.useParams()
  const tools = useMemo(
    () =>
      uniqueSorted([
        ...BACKEND_CHAT_TOOL_NAMES,
        ...CHAT_TOOL_UI_NAMES,
        ...WATCHLIST_TOOLS,
      ]),
    []
  )
  const [query, setQuery] = useState('')
  const [selectedTool, setSelectedTool] = useState('risksList')
  const filteredTools = tools.filter((tool) =>
    tool.toLowerCase().includes(query.toLowerCase())
  )
  const uxSpec = getToolUxSpec(selectedTool, workspaceId)

  if (!import.meta.env.DEV) {
    return (
      <div className='p-8 text-sm text-text-body'>
        Tool UI harness is only available in development.
      </div>
    )
  }

  return (
    <ChatStoppedContext.Provider value={false}>
      <div className='flex h-screen overflow-hidden bg-background text-text-secondary'>
        <aside className='flex h-screen w-[320px] shrink-0 flex-col overflow-hidden border-r border-border bg-card'>
          <div className='shrink-0 border-b border-border p-4'>
            <div className='text-[10px] font-semibold tracking-[0.12em] text-text-subtle uppercase'>
              Dev only
            </div>
            <h1 className='mt-1 text-[22px] font-semibold'>Tool UI Harness</h1>
            <p className='mt-1 text-xs text-text-subtle'>
              Backend chat tools rendered through the real UI dispatcher.
            </p>
            <label className='mt-4 flex h-9 items-center gap-2 rounded-md border border-border-divider bg-card px-3'>
              <Search className='h-4 w-4 text-text-subtle' />
              <input
                className='min-w-0 flex-1 bg-transparent text-xs outline-none'
                onChange={(event) => setQuery(event.target.value)}
                placeholder='Search tools'
                value={query}
              />
            </label>
          </div>
          <div
            className='overscroll-contain p-2'
            style={{ height: 'calc(var(--app-vh) - 150px)', overflowY: 'scroll' }}
          >
            {filteredTools.map((tool) => {
              const isSelected = tool === selectedTool
              const isWatchlist = WATCHLIST_TOOLS.includes(tool)
              return (
                <button
                  className={`mb-1 flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-xs ${
                    isSelected
                      ? 'bg-[#EDF2FF] text-cobalt-strong'
                      : 'text-text-body hover:bg-background'
                  }`}
                  key={tool}
                  onClick={() => setSelectedTool(tool)}
                  type='button'
                >
                  <span className='truncate font-mono'>{tool}</span>
                  {isWatchlist ? (
                    <span className='ml-2 rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[10px] font-medium text-[#C2410C]'>
                      watch
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </aside>

        <main className='min-w-0 flex-1 overflow-y-auto p-8'>
          <div className='mb-6 flex items-end justify-between gap-4'>
            <div>
              <div className='text-[10px] font-semibold tracking-[0.12em] text-text-subtle uppercase'>
                Selected tool
              </div>
              <h2 className='mt-1 font-mono text-[24px] font-semibold'>
                {selectedTool}
              </h2>
            </div>
            <div className='rounded-sm border border-border bg-card px-3 py-2 text-xs text-text-body'>
              Fallback means the tool has no mapped UI.
            </div>
          </div>

          <section className='mb-5 rounded-sm border border-border bg-card p-4'>
            <div className='mb-3 flex items-start justify-between gap-4'>
              <div>
                <h3 className='text-sm font-semibold'>Frontend UX</h3>
                <p className='mt-1 text-xs text-text-subtle'>
                  What this tool should do for the user outside the mock states.
                </p>
              </div>
              {uxSpec.href ? (
                <a
                  className='rounded-sm border border-border-divider px-2.5 py-1.5 text-xs font-medium text-cobalt-strong hover:bg-[#EDF2FF]'
                  href={uxSpec.href}
                >
                  Open surface
                </a>
              ) : null}
            </div>
            <div className='grid gap-3 text-xs md:grid-cols-2 xl:grid-cols-4'>
              <UxInfo label='Intent' value={uxSpec.intent} />
              <UxInfo label='Chat UX' value={uxSpec.chatUx} />
              <UxInfo label='Product Surface' value={uxSpec.productSurface} />
              <UxInfo label='How to Test' value={uxSpec.manualTest} />
            </div>
          </section>

          <div className='grid gap-4 lg:grid-cols-3'>
            <ToolStateCard
              label='Running'
              part={makeToolPart(selectedTool, 'input-available')}
              state='input-available'
              toolName={selectedTool}
            />
            <ToolStateCard
              label='Completed'
              part={makeToolPart(selectedTool, 'output-available')}
              state='output-available'
              toolName={selectedTool}
            />
            <ToolStateCard
              label='Error'
              part={makeToolPart(selectedTool, 'error')}
              state='error'
              toolName={selectedTool}
            />
          </div>
        </main>
      </div>
    </ChatStoppedContext.Provider>
  )
}

function UxInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-sm border border-border bg-surface-subtle p-3'>
      <div className='mb-1 text-[10px] font-semibold tracking-[0.12em] text-text-subtle uppercase'>
        {label}
      </div>
      <p className='leading-5 text-text-body'>{value}</p>
    </div>
  )
}

function ToolStateCard({
  label,
  part,
  state,
  toolName,
}: {
  label: string
  part: any
  state: string
  toolName: string
}) {
  return (
    <section className='rounded-sm border border-border bg-card p-4'>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <h3 className='text-sm font-semibold'>{label}</h3>
        <span className='rounded-full bg-surface-muted px-2 py-0.5 font-mono text-[10px] text-text-subtle'>
          {state}
        </span>
      </div>
      <div className='min-h-[120px] rounded-sm border border-border bg-surface-subtle p-3'>
        {renderToolPart(part, `dev-${toolName}`, 0, {
          isLastAssistantMessage: true,
        })}
      </div>
    </section>
  )
}
